import DefaultHeader from "../../../../shared/DefaultHeader";

import React, { useEffect, useState } from "react";
import { applicationAddEditUser } from "../../../../services/applicationService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { jobDetailsUser } from "../../../../services/postService";
import headerBg from "../../../../assets/image/headerBgJob.jpg";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { formattedDate } from "../../../../helpers";
import { FileText, Upload, X } from "lucide-react";

const Section = ({ title, children }) => {
  const isString = typeof children === "string";

  return (
    <div className="mb-6">
      <h4 className="text-2xl font-semibold mb-2">{title}</h4>

      {isString ? (
        <div className="text-gray-700 mb-2" dangerouslySetInnerHTML={{ __html: children }} />
      ) : (
        <div className="text-gray-700 mb-2">{children}</div>
      )}
    </div>
  );
};

const SummaryItem = ({ label, value }) => (
  <p className="text-md text-gray-600">
    <i className="fa fa-angle-right text-emeraldPrimary mr-2"></i>
    <span className="font-medium">{label}:</span> {value}
  </p>
);

const JobApply = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("job");
  const user = useSelector((state) => state.profile?.data);
  const navigate = useNavigate();

  const { data: details } = useQuery({
    queryKey: ["application-details", id],
    queryFn: async () => {
      const res = await jobDetailsUser({ id: id });
      return res?.data;
    },
    enabled: !!id,
  });

  const [formData, setFormData] = useState({
    job: "",
    jobMode: "standard",
    applicant: user?._id,
    message: "",
    attachments: [],
  });

  useEffect(() => {
    setFormData((prev) => ({ ...prev, job: details?._id }));
  }, [details]);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
    }));

    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments],
    }));
  };

  const removeAttachment = (id) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((att) => att.id !== id),
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.job.trim()) {
      newErrors.job = "Job ID is required";
    }

    if (!formData.applicant.trim()) {
      newErrors.applicant = "Applicant ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append("job", formData.job);
    formDataToSend.append("jobMode", formData.jobMode);
    formDataToSend.append("applicant", formData.applicant);
    formDataToSend.append("message", formData.message);

    formData.attachments.forEach((att) => {
      formDataToSend.append("attachments[]", att.file);
    });

    mutate(formDataToSend);
  };

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (formDataToSend) => {
      return await applicationAddEditUser(formDataToSend);
    },

    onSuccess: (res) => {
      //   getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        toast.success("Application Submitted Successfully!");
        navigate(`/user/jobs`);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <>
      <div className="mx-6 -my-14 py-12 px-4 sm:px-6 lg:px-8">
        <DefaultHeader />
        <div className="relative bg-cover bg-center min-h-[400px]" style={{ backgroundImage: `url(${headerBg})` }}>
          <div className="absolute inset-0 bg-black/50" /> {/* Optional overlay */}
          <div className="relative mx-auto px-6 py-20">
            <div className="border-l-8 my-4 border-emerald-500 pl-6">
              <h1 className="text-7xl font-extrabold text-white animate-slide-in-down">Job Detail</h1>
              <div className="mt-4 text-lg text-white uppercase tracking-wide space-x-1 animate-slide-in-up">
                <span className="text-emerald-400">Explore</span>
                <span>{" > "}</span>
                <span className="text-emerald-400">Discover</span>
                <span>{" > "}</span>
                <span className="text-white">Apply</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto py-12 animate-slide-in-up mt-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <div className="flex items-center mb-8">
                <img src={details?.coverMedia?.url} alt="Company Logo" className="w-20 h-20 rounded border object-cover" />
                <div className="ps-6">
                  <h3 className="text-2xl font-semibold mb-2">{details?.title}</h3>
                  <div className="flex flex-wrap gap-6 text-gray-600 text-md">
                    <span>
                      <i className="fa fa-map-marker-alt text-emeraldPrimary mr-2"></i>
                      {details?.location[0]?.defaultAddress
                        ? `${details?.location.find((loc) => loc.defaultAddress === true)?.city} , ${
                            details?.location.find((loc) => loc.defaultAddress === true)?.country
                          }`
                        : "Not specified"}
                    </span>
                    <span>
                      <i className="far fa-clock text-emeraldPrimary mr-2"></i>
                      {details?.job?.jobType ? details?.job?.jobType.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Not mentioned"}
                    </span>
                    <span>
                      <i className="far fa-money-bill-alt text-emeraldPrimary mr-2"></i>
                      {details?.job?.budget
                        ? `${details?.job?.budget?.range?.unit === "usd" ? "$" : "₹"}${details?.job?.budget?.range?.min} - ${
                            details?.job?.budget?.range?.unit === "usd" ? "$" : "₹"
                          }${details?.job?.budget?.range?.max}`
                        : "TBD"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-10">
                <Section title="Job description">{details?.content ? details?.content : "No description"}</Section>

                <Section title="Required Skills">
                  {details?.job?.skills.length > 0 ? (
                    <>
                      <p>Essential skills needed to excel in this role.</p>
                      <ul className="list-disc pl-5 text-emerald-400 mt-3">
                        {details?.job?.skills.map((item, i) => (
                          <li className="text-gray-700" key={i}>
                            {item?.title}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    "No Skill Required"
                  )}
                </Section>
              </div>

              <div>
                <h4 className="text-2xl font-semibold mb-4">Apply For The Job</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="text-sm font-medium text-gray-800 mb-1">Applicant Name</label>
                  <label className="text-sm font-medium text-gray-800 mb-1">Applicant Email</label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      className="form-input rounded"
                      value={`${user?.firstName} ${user?.lastName}`}
                      placeholder="Your Name"
                      disabled
                    />
                    <input type="email" className="form-input rounded" value={`${user?.email}`} placeholder="Your Email" disabled />
                  <h2 className="text-sm font-medium text-gray-800 mt-5 flex items-center">
                    <FileText className="mr-2" size={20} />
                    Cover Letter / Message
                  </h2>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="5"
                    className="form-textarea sm:col-span-2 rounded"
                    placeholder="Coverletter"
                  ></textarea>

                  <div className="space-y-4">
                    <h2 className="text-sm font-medium text-gray-800 mt-5 flex items-center">
                      <Upload className="mr-2" size={20} />
                      Attachments (Upload Files [CV, Portfolio etc.])
                    </h2>
                    <div>
                      <input
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.zip"
                      />
                      <p className="text-sm text-gray-500 mt-1">Supported formats: JPG, PNG</p>
                    </div>
                  </div>
                  {formData.attachments.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700 mt-5">Uploaded Files:</p>
                      {formData.attachments.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-3 bg-white border rounded-md">
                          <div className="flex items-center space-x-3">
                            {file.file.type.startsWith("image/") ? (
                              <img src={URL.createObjectURL(file.file)} alt={file.name} className="w-8 h-8 object-cover rounded" />
                            ) : (
                              <FileText size={16} className="text-gray-500" />
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button type="button" onClick={() => removeAttachment(file.id)} className="text-red-500 hover:text-red-700">
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`btn btn-primary bg-${isSubmitting?"gray-400":"emeraldPrimary"} hover:bg-${isSubmitting?"gray-400":"emerald-500"} text-white font-bold w-full sm:col-span-2 pt-2 pb-2`}
                  >
                    {isSubmitting ? "Applying..." : "Apply Now"}
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 space-y-6">
              <div className="bg-emerald-50 p-12 rounded animate-slide-in-up flex flex-col items-start gap-y-3">
                <h4 className="text-xl font-semibold mb-4">Job Summary</h4>
                <SummaryItem label="Published On" value={formattedDate(details?.createdAt)} />
                <SummaryItem label="Vacancy" value={details?.job?.openings} />
                <SummaryItem
                  label="Job Nature"
                  value={details?.job?.jobType ? details?.job?.jobType.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Not mentioned"}
                />
                <SummaryItem
                  label="Salary Range"
                  value={
                    details?.job?.budget
                      ? `${details?.job?.budget?.range?.unit === "usd" ? "$" : "₹"}${details?.job?.budget?.range?.min} - ${
                          details?.job?.budget?.range?.unit === "usd" ? "$" : "₹"
                        }${details?.job?.budget?.range?.max}`
                      : "TBD"
                  }
                />
                <SummaryItem label="Location" value="New York, USA" />
              </div>

              <div className="bg-emerald-50 p-6 rounded animate-slide-in-up flex flex-col items-start gap-y-3">
                <h4 className="text-xl font-semibold mb-4">Company Detail</h4>
                <p>
                  Ipsum dolor ipsum accusam stet et et diam dolores, sed rebum sadipscing elitr vero dolores. Lorem dolore elitr justo et no gubergren
                  sadipscing, ipsum et takimata aliquyam et rebum est ipsum lorem diam.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default JobApply;
